/* BARROS — COLLECTION CONTROLS V6 / ADAPTIVE FILTER FRAMEWORK */
(() => {
  'use strict';

  const BASE_FILTER_ORDER = ['price', 'availability'];

  const FILTER_LABELS = {
    brand: 'Marca',
    motor: 'Motore',
    battery: 'Batteria',
    material: 'Materiale',
    gearing: 'Trasmissione',
    suspension: 'Sospensione',
    'front-travel': 'Escursione anteriore',
    'rear-travel': 'Escursione posteriore',
    'component-travel': 'Escursione',
    size: 'Taglia',
    'accessory-size': 'Taglia',
    wheels: 'Ruote',
    'bike-compatibility': 'Compatibilità bici',
    'brake-type': 'Tipo freno',
    price: 'Prezzo',
    availability: 'Disponibilità'
  };

  const EBIKE_FILTER_ORDER = [
    'brand',
    'motor',
    'battery',
    'front-travel',
    'rear-travel',
    'size',
    'wheels',
    'price',
    'availability'
  ];

  const BIKE_FILTER_ORDER = [
    'size',
    'material',
    'gearing',
    'suspension',
    'price',
    'availability'
  ];

  const EQUIPMENT_SIZE_FILTER_ORDER = [
    'size',
    'price',
    'availability'
  ];

  const COMPONENT_FILTER_ORDER = [
    'bike-compatibility',
    'price',
    'availability'
  ];

  const BRAKE_COMPONENT_FILTER_ORDER = [
    'bike-compatibility',
    'brake-type',
    'price',
    'availability'
  ];

  const DROPPER_COMPONENT_FILTER_ORDER = [
    'bike-compatibility',
    'component-travel',
    'price',
    'availability'
  ];

  const ACCESSORY_FILTER_ORDER = [
    'bike-compatibility',
    'price',
    'availability'
  ];

  const EQUIPMENT_SIZE_HANDLE_PATTERN = /(casch|scarp|guant|calzin|ginocch|gomit|pettorin|pantal|magli|giacch|gilet|intim|felp|protez|abbigli)/i;
  const BIKE_HANDLE_PATTERN = /^(biciclette|bike-(?:full|front)|gravel|strada|mtb(?:-|$)|mountain-bike(?:-|$)|urban(?:-|$)|trekking(?:-|$))/i;
  const COMPONENT_HANDLE_PATTERN = /(componenti-bike|copert|pneumatic|fren|pastigl|disch|trasmission|catena|casset|coron|guarnitur|cambio|deragli|pedal|manub|attac|reggisell|sospension|forcell|ammortizz|ruot|mozzo|cerchi|raggi|movimento|serie-sterzo)/i;
  const BRAKE_HANDLE_PATTERN = /(fren|pastigl|disch)/i;
  const DROPPER_HANDLE_PATTERN = /(reggisell)/i;
  const ACCESSORY_HANDLE_PATTERN = /(accessori-bike|ciclocomputer|luci|luce|attrezz|attrezzi|pompe|pompa|borse|borsa|portapacchi|manutenz|borracc|support|lucchett|parafang|campanell)/i;

  const PROFILE_CACHE = new Map();

  const STICKY_HEADER_SELECTORS = [
    '#shopify-section-header',
    '.shopify-section-header',
    '.shopify-section-header-sticky',
    '.shopify-section-group-header-group',
    '.header-wrapper',
    '.barros-header',
    '[data-sticky-header]',
    'sticky-header',
    'header[role="banner"]'
  ];

  function makeProfile(order) {
    const ordered = ['brand', ...order.filter((key) => key !== 'brand')];
    return { order: ordered, allowed: new Set(ordered) };
  }

  function getCollectionFilterProfile(handle = '') {
    const normalized = handle.trim().toLowerCase();
    if (PROFILE_CACHE.has(normalized)) return PROFILE_CACHE.get(normalized);

    let order = BASE_FILTER_ORDER;

    if (/^(e-bike(?:-|$)|e-mtb(?:-|$)|light-e-mtb(?:-|$))/.test(normalized)) {
      order = EBIKE_FILTER_ORDER;
    } else if (BIKE_HANDLE_PATTERN.test(normalized)) {
      order = BIKE_FILTER_ORDER;
    } else if (normalized === 'equipaggiamento') {
      order = BASE_FILTER_ORDER;
    } else if (EQUIPMENT_SIZE_HANDLE_PATTERN.test(normalized)) {
      order = EQUIPMENT_SIZE_FILTER_ORDER;
    } else if (COMPONENT_HANDLE_PATTERN.test(normalized)) {
      if (DROPPER_HANDLE_PATTERN.test(normalized)) order = DROPPER_COMPONENT_FILTER_ORDER;
      else if (BRAKE_HANDLE_PATTERN.test(normalized)) order = BRAKE_COMPONENT_FILTER_ORDER;
      else order = COMPONENT_FILTER_ORDER;
    } else if (ACCESSORY_HANDLE_PATTERN.test(normalized)) {
      order = ACCESSORY_FILTER_ORDER;
    }

    const profile = makeProfile(order);
    PROFILE_CACHE.set(normalized, profile);
    return profile;
  }

  function positionSortMenu(details) {
    if (!details?.open) return;
    const trigger = details.querySelector('.barros-sort__trigger');
    const menu = details.querySelector('.barros-sort__menu');
    if (!trigger || !menu) return;

    details.classList.remove('barros-sort__details--up');
    menu.style.removeProperty('--barros-sort-max-height');

    requestAnimationFrame(() => {
      if (!details.open) return;
      const rect = trigger.getBoundingClientRect();
      const menuHeight = menu.scrollHeight;
      const below = Math.max(0, window.innerHeight - rect.bottom - 16);
      const above = Math.max(0, rect.top - 16);
      const desired = Math.min(menuHeight, 460);
      const openUp = below < desired && above > below;
      const available = openUp ? above : below;
      const maxHeight = Math.max(160, Math.min(menuHeight, Math.max(160, available - 10)));

      details.classList.toggle('barros-sort__details--up', openUp);
      menu.style.setProperty('--barros-sort-max-height', `${maxHeight}px`);
    });
  }

  function positionOpenSorters() {
    document.querySelectorAll('.barros-sort__details[open]').forEach(positionSortMenu);
  }

  function initSorters(scope = document) {
    scope.querySelectorAll('[data-barros-sort]').forEach((sorter) => {
      const select = sorter.querySelector('[data-barros-sort-select]');
      const label = sorter.querySelector('[data-barros-sort-label]');
      const details = sorter.querySelector('.barros-sort__details');
      const options = Array.from(sorter.querySelectorAll('[data-barros-sort-option]'));
      if (!select || !options.length) return;

      if (details && details.dataset.barrosSortPositionReady !== 'true') {
        details.addEventListener('toggle', () => {
          if (details.open) positionSortMenu(details);
          else details.classList.remove('barros-sort__details--up');
        });
        details.dataset.barrosSortPositionReady = 'true';
      }
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-barros-sort-option]');
    if (!button) return;
    const sorter = button.closest('[data-barros-sort]');
    const select = sorter?.querySelector('[data-barros-sort-select]');
    const value = button.dataset.barrosSortOption;
    if (!select || !value) return;
    select.value = value;
    sorter.querySelectorAll('[data-barros-sort-option]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', String(active));
    });
    const label = sorter.querySelector('[data-barros-sort-label]');
    if (label) label.textContent = select.options[select.selectedIndex].textContent.trim();
    sorter.querySelector('.barros-sort__details')?.removeAttribute('open');
    select.dispatchEvent(new Event('input', { bubbles: true }));
  });

  function getCollectionHandle(node = document) {
    const container = node.closest?.('.facets-container') || document.querySelector('.facets-container[data-collection-handle]');
    return (container?.dataset.collectionHandle || '').trim();
  }

  function getFilterKey(details) {
    if (!details) return null;
    const inputs = Array.from(details.querySelectorAll('input[name]'));
    const names = inputs.map((input) => input.name || '').filter(Boolean);
    const lowerNames = names.map((name) => name.toLowerCase());
    const id = (details.id || '').toLowerCase();
    const text = (details.querySelector('summary')?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();

    if (names.some((name) => name === 'filter.p.vendor') || /^(vendor|marca)\b/.test(text)) return 'brand';
    if (lowerNames.some((name) => name.includes('barros_pdp.e_system')) || /e-system|motore/.test(text)) return 'motor';
    if (lowerNames.some((name) => name.includes('shopify.battery-size')) || /capacità batteria|battery size|^batteria\b/.test(text)) return 'battery';

    if (lowerNames.some((name) => name.includes('shopify.material')) || /^(materiale|material)\b/.test(text)) return 'material';
    if (lowerNames.some((name) => name.includes('shopify.gearing-type')) || /gearing|ingranagg|^trasmissione\b/.test(text)) return 'gearing';
    if (lowerNames.some((name) => name.includes('shopify.bicycle-suspension-type')) || /(?:tipo.*sospension|bicycle suspension|^sospensione\b)/.test(text)) return 'suspension';
    if (lowerNames.some((name) => name.includes('shopify.compatible-bike-type')) || /(?:compatib.*bici|bici.*compatib|compatible.*bike)/.test(text)) return 'bike-compatibility';
    if (lowerNames.some((name) => name.includes('shopify.bicycle-brake-type')) || /(?:tipo.*fren|bicycle brake|brake type|^tipo freno\b)/.test(text)) return 'brake-type';
    if (lowerNames.some((name) => name.includes('shopify.accessory-size')) || /(?:accessory size|taglia accessorio)/.test(text)) return 'accessory-size';

    if (lowerNames.some((name) => name.includes('custom.escursione_anteriore')) || /escursione anteriore/.test(text)) return 'front-travel';
    if (lowerNames.some((name) => name.includes('custom.escursione_posteriore')) || /escursione posteriore/.test(text)) return 'rear-travel';
    if (lowerNames.some((name) => name.includes('custom.formato_ruote')) || /formato ruote|^ruote\b/.test(text)) return 'wheels';
    if (names.some((name) => name === 'filter.v.availability') || /availability|disponibilità/.test(text)) return 'availability';
    if (lowerNames.some((name) => name.includes('filter.v.price')) || id.includes('price') || /^prezzo\b|^price\b/.test(text)) return 'price';

    const optionNames = lowerNames.filter((name) => name.startsWith('filter.v.option.'));
    if (optionNames.some((name) => /taglia|size/.test(name)) || /^(taglia|size)\b/.test(text)) return 'size';
    if (optionNames.some((name) => /escursione|travel/.test(name)) || /^(escursione|travel)\b/.test(text)) return 'component-travel';

    if (
      lowerNames.some((name) => /^filter\.p\.(product_type|category)/i.test(name)) ||
      /^(categoria|categorie|category|categories)\b/.test(text)
    ) return 'category';

    return null;
  }

  function setFilterLabel(details, label) {
    if (!details || !label) return;
    const selectors = [
      '.mobile-facets__summary > div > span:first-child',
      '.facets__summary-label',
      '.facets__summary > div > span:first-child',
      '.mobile-facets__close-button > span:not(.svg-wrapper)',
      'legend.visually-hidden'
    ];
    selectors.forEach((selector) => {
      const node = details.querySelector(selector);
      if (node && node.textContent.trim() !== label) node.textContent = label;
    });
  }

  function getFilterDetails(scope = document) {
    return Array.from(scope.querySelectorAll('.mobile-facets__details, #FacetsWrapperDesktop details.js-filter'))
      .filter((details) => !((details.id || '').includes('SortBy')));
  }

  function normalizeTravelUnits(details) {
    details.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const label = input.closest('label');
      const visible = label?.querySelector('.facet-checkbox__text-label');
      if (!visible) return;
      const raw = visible.textContent.trim();
      const match = raw.match(/^(\d+(?:[.,]\d+)?)(?:\s*mm)?$/i);
      if (match) visible.textContent = `${match[1]} mm`;
    });
  }

  function normalizeFilterLabels(scope = document) {
    getFilterDetails(scope).forEach((details) => {
      const key = getFilterKey(details);
      if (key && FILTER_LABELS[key]) {
        details.dataset.barrosFilterKey = key;
        setFilterLabel(details, FILTER_LABELS[key]);
        if (key === 'front-travel' || key === 'rear-travel' || key === 'component-travel') normalizeTravelUnits(details);
      }
    });

    scope.querySelectorAll('.mobile-facets__heading, .facets__heading--vertical').forEach((heading) => {
      if (heading.textContent.trim() !== 'Filtra per caratteristiche') heading.textContent = 'Filtra per caratteristiche';
    });
  }

  function normalizeProductCount(scope = document) {
    scope.querySelectorAll('#ProductCount, .product-count__text span[id^="ProductCount"]').forEach((countNode) => {
      const raw = countNode.textContent.replace(/\s+/g, ' ').trim();
      const values = (raw.match(/\d[\d.,]*/g) || [])
        .map((value) => Number(value.replace(/\D/g, '')))
        .filter((value) => Number.isFinite(value));
      if (!values.length) return;

      const current = values[0];
      const total = values.length > 1 ? values[1] : current;
      const next = current < total ? `${current} di ${total} prodotti` : `${current} ${current === 1 ? 'prodotto' : 'prodotti'}`;
      if (countNode.textContent.trim() !== next) countNode.textContent = next;
    });
  }

  function normalizeActions(scope = document) {
    scope.querySelectorAll('#FacetsWrapperMobile > .mobile-facets__footer').forEach((footer) => {
      const clear = footer.querySelector('.mobile-facets__clear');
      const close = footer.querySelector('.button');
      if (clear && clear.textContent.trim() !== 'Azzera filtri') clear.textContent = 'Azzera filtri';
      if (close && close.textContent.trim() !== 'Chiudi') close.textContent = 'Chiudi';
    });

    scope.querySelectorAll('.facets-pill .active-facets, .active-facets-mobile').forEach((group) => {
      const activeButtons = group.querySelectorAll('a.active-facets__button').length;
      const wrapper = group.querySelector('.active-facets__button-wrapper');
      const reset = wrapper?.querySelector('.active-facets__button-remove');
      if (!wrapper || !reset) return;
      if (reset.textContent.trim() !== 'Azzera') reset.textContent = 'Azzera';
      wrapper.hidden = activeButtons < 2;
    });
  }

  function relabelAvailability(scope = document) {
    scope.querySelectorAll('input[name="filter.v.availability"]').forEach((input) => {
      const label = input.closest('label');
      if (!label) return;
      const replacement = input.value === '0' ? 'In arrivo' : input.value === '1' ? 'Disponibile' : null;
      if (!replacement) return;
      const visibleLabel = label.querySelector('.facet-checkbox__text-label');
      if (visibleLabel && visibleLabel.textContent.trim() !== replacement) visibleLabel.textContent = replacement;
      const accessibleLabel = label.querySelector('.visually-hidden');
      if (accessibleLabel) {
        const current = accessibleLabel.textContent.trim();
        const countMatch = current.match(/\([^)]*\)\s*$/);
        const next = `${replacement}${countMatch ? ` ${countMatch[0]}` : ''}`;
        if (current !== next) accessibleLabel.textContent = next;
      }
    });
  }

  function facetOptionSignature(details) {
    return Array.from(details.querySelectorAll('input[type="checkbox"]'))
      .map((input) => {
        const labelText = input.closest('label')?.textContent.replace(/\s+/g, ' ').trim() || '';
        const count = labelText.match(/\((\d+)\)\s*$/)?.[1] || '';
        return `${input.value}|${count}|${input.disabled ? 'disabled' : 'enabled'}`;
      })
      .sort()
      .join('||');
  }

  function combineSymmetricTravelFilters(scope = document) {
    scope.querySelectorAll('#FacetsWrapperMobile, #FacetsWrapperDesktop').forEach((wrapper) => {
      const details = Array.from(wrapper.children).filter((node) =>
        node.matches?.('.mobile-facets__details, details.js-filter') && !(node.id || '').includes('SortBy')
      );
      const front = details.find((node) => getFilterKey(node) === 'front-travel');
      const rear = details.find((node) => getFilterKey(node) === 'rear-travel');
      if (!front || !rear) return;

      front.removeAttribute('data-barros-travel-combined');
      rear.removeAttribute('data-barros-travel-combined-hidden');

      const frontInputs = Array.from(front.querySelectorAll('input[type="checkbox"]'));
      const rearInputs = Array.from(rear.querySelectorAll('input[type="checkbox"]'));
      const rearHasActiveValue = rearInputs.some((input) => input.checked);
      const sameOptions =
        frontInputs.length > 1 &&
        frontInputs.length === rearInputs.length &&
        facetOptionSignature(front) === facetOptionSignature(rear);

      if (front.hidden || rear.hidden || rearHasActiveValue || !sameOptions) return;

      setFilterLabel(front, 'Escursione');
      normalizeTravelUnits(front);
      front.dataset.barrosTravelCombined = 'true';
      rear.dataset.barrosTravelCombinedHidden = 'true';
      rear.hidden = true;
    });
  }

  function relabelActiveFacets(scope = document) {
    const travelCombined = !!document.querySelector('[data-barros-travel-combined="true"]');

    scope.querySelectorAll('.active-facets__button-inner').forEach((pill) => {
      const textNode = Array.from(pill.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (!textNode) return;
      let text = textNode.textContent;

      if (travelCombined) {
        text = text.replace(/^(Escursione anteriore):/i, 'Escursione:');
        text = text.replace(/^(Escursione:\s*\d+(?:[.,]\d+)?)(?!\s*mm)\s*$/i, '$1 mm');
      }

      const replacements = [
        [/^(Availability|Disponibilità):\s*Esaurito/i, 'Disponibilità: In arrivo'],
        [/^(Vendor|Marca):/i, 'Marca:'],
        [/^(Barros PDP\s*[—-]\s*E-System|E-System|Motore):/i, 'Motore:'],
        [/^(Capacità batteria|Battery size|Batteria):/i, 'Batteria:'],
        [/^(Materiale|Material):/i, 'Materiale:'],
        [/^(Gearing type|Tipo(?: di)? ingranaggio|Trasmissione):/i, 'Trasmissione:'],
        [/^(Bicycle suspension type|Tipo(?: di)? sospensione(?: della bicicletta)?|Sospensione):/i, 'Sospensione:'],
        [/^(Compatible bike type|Tipo(?: di)? bicicletta compatibile|Compatibilità bici):/i, 'Compatibilità bici:'],
        [/^(Bicycle brake type|Tipo(?: di)? freno(?: della bicicletta)?|Tipo freno):/i, 'Tipo freno:'],
        [/^(Formato ruote|Ruote):/i, 'Ruote:'],
        [/^(TAGLIA|Taglia|Size):/i, 'Taglia:'],
        [/^(ESCURSIONE|Escursione|Travel):/i, 'Escursione:'],
        [/^(Escursione anteriore):/i, 'Escursione anteriore:'],
        [/^(Escursione Posteriore|Escursione posteriore):/i, 'Escursione posteriore:'],
        [/^(Availability|Disponibilità):/i, 'Disponibilità:']
      ];
      replacements.forEach(([pattern, replacement]) => { text = text.replace(pattern, replacement); });
      text = text.replace(/^(Marca|Motore|Batteria|Materiale|Trasmissione|Sospensione|Compatibilità bici|Tipo freno|Ruote|Taglia|Escursione(?: anteriore| posteriore)?|Disponibilità):\s*/i, '$1 · ');
      if (text !== textNode.textContent) textNode.textContent = text;
    });
  }

  function hideIrrelevantFilters(scope = document) {
    getFilterDetails(scope).forEach((details) => {
      const handle = getCollectionHandle(details);
      const profile = getCollectionFilterProfile(handle);
      const key = getFilterKey(details);
      const shouldHide = !key || !profile.allowed.has(key);
      if (details.hidden !== shouldHide) details.hidden = shouldHide;
    });
  }

  function hideRedundantFilters(scope = document) {
    getFilterDetails(scope).forEach((details) => {
      if (details.hidden) return;
      const checkboxes = Array.from(details.querySelectorAll('input[type="checkbox"]'));
      if (!checkboxes.length) return;
      const isAvailability = checkboxes.some((input) => input.name === 'filter.v.availability');
      const shouldHide = !isAvailability && checkboxes.length === 1;
      if (details.hidden !== shouldHide) details.hidden = shouldHide;
    });
  }

  function reorderWrapper(wrapper) {
    const details = Array.from(wrapper.children).filter((node) =>
      node.matches?.('.mobile-facets__details, details.js-filter') && !(node.id || '').includes('SortBy')
    );
    if (!details.length) return;

    const handle = getCollectionHandle(wrapper);
    const profile = getCollectionFilterProfile(handle);
    const order = profile.order;

    const sorted = details
      .map((node, originalIndex) => ({ node, originalIndex, key: getFilterKey(node) }))
      .sort((a, b) => {
        const aIndex = order.indexOf(a.key);
        const bIndex = order.indexOf(b.key);
        const aRank = aIndex === -1 ? order.length + a.originalIndex : aIndex;
        const bRank = bIndex === -1 ? order.length + b.originalIndex : bIndex;
        return aRank - bRank;
      })
      .map(({ node }) => node);

    if (sorted.every((node, index) => node === details[index])) return;
    const anchor = Array.from(wrapper.children).find((node) =>
      (node.id || '').includes('SortBy') || node.classList?.contains('mobile-facets__footer')
    );
    sorted.forEach((node) => wrapper.insertBefore(node, anchor || null));
  }

  function reorderFilters(scope = document) {
    scope.querySelectorAll('#FacetsWrapperMobile, #FacetsWrapperDesktop').forEach(reorderWrapper);
  }

  function getStickyHeaderBottom() {
    const candidates = new Set();
    STICKY_HEADER_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => candidates.add(node));
    });

    let bottom = 0;
    candidates.forEach((node) => {
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return;
      if (style.position !== 'fixed' && style.position !== 'sticky') return;

      const rect = node.getBoundingClientRect();
      if (rect.height <= 0 || rect.bottom <= 0 || rect.top >= window.innerHeight) return;

      if (style.position === 'sticky') {
        const declaredTop = Number.parseFloat(style.top);
        const stickyTop = Number.isFinite(declaredTop) ? declaredTop : 0;
        if (rect.top > stickyTop + 3) return;
      }

      bottom = Math.max(bottom, rect.bottom);
    });

    return Math.max(0, Math.round(bottom));
  }

  function updateStickyControls() {
    const offset = getStickyHeaderBottom();
    document.documentElement.style.setProperty('--barros-sticky-offset', `${offset}px`);

    document.querySelectorAll('.barros-collection--products #main-collection-filters').forEach((aside) => {
      const rect = aside.getBoundingClientRect();
      const stuck = window.scrollY > 0 && rect.top <= offset + 1;
      aside.classList.toggle('is-stuck', stuck);
    });
  }

  function refresh(scope = document) {
    initSorters(scope);
    normalizeFilterLabels(scope);
    normalizeProductCount(scope);
    normalizeActions(scope);
    relabelAvailability(scope);
    hideIrrelevantFilters(scope);
    hideRedundantFilters(scope);
    combineSymmetricTravelFilters(scope);
    relabelActiveFacets(scope);
    reorderFilters(scope);
    updateStickyControls();
  }

  let refreshQueued = false;
  function queueRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      refreshQueued = false;
      refresh(document);
    });
  }

  let viewportUpdateQueued = false;
  function queueViewportUpdate() {
    if (viewportUpdateQueued) return;
    viewportUpdateQueued = true;
    requestAnimationFrame(() => {
      viewportUpdateQueued = false;
      updateStickyControls();
      positionOpenSorters();
    });
  }

  const init = () => {
    refresh(document);
    if (document.body) {
      const observer = new MutationObserver(queueRefresh);
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  window.addEventListener('resize', queueViewportUpdate, { passive: true });
  window.addEventListener('scroll', queueViewportUpdate, { passive: true });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.barros-sort__details[open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });
})();

